// src/components/EventModal.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from '../styles/EventModal.module.css';

const serviceTypes = [
    { key: 'design', label: 'Design' },
    { key: 'reelContentCreation', label: 'Reel (Content Creation)' },
    { key: 'reelPro', label: 'Reel (Professional)' },
    { key: 'video', label: 'Video Production' },
    { key: 'motionGraphics', label: 'Motion Graphics' },
    { key: 'cgi', label: 'CGI Elements' },
    { key: 'sponsorshipManagement', label: 'Sponsorship Management' },
    { key: 'printsJob', label: 'Prints Job' },
    { key: 'meeting', label: 'Meeting' },
    { key: 'other', label: 'Other Task' }
];

const EventModal = ({ 
    isOpen, 
    onClose, 
    eventData, 
    slotInfo, 
    onSave, 
    onDelete, 
    employees = [] 
}) => {
    const [title, setTitle] = useState('');
    const [serviceType, setServiceType] = useState('');
    const [start, setStart] = useState(new Date());
    const [end, setEnd] = useState(new Date());
    const [allDay, setAllDay] = useState(true);
    const [notes, setNotes] = useState('');
    const [assignedTo, setAssignedTo] = useState([]);
    const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const isMounted = useRef(true);

    const isValidDate = (date) => date && !isNaN(new Date(date).getTime());
    const safeDate = (date, fallback = new Date()) => isValidDate(date) ? new Date(date) : fallback;

    const assignableEmployees = useMemo(() => {
        if (!Array.isArray(employees)) return [];
        return employees.filter(emp => emp.role?.toLowerCase() !== 'trainee');
    }, [employees]);

    const filteredEmployees = useMemo(() => {
        if (!assigneeSearchTerm) return assignableEmployees;
        const searchTerm = assigneeSearchTerm.toLowerCase();
        return assignableEmployees.filter(emp =>
            (emp.name || '').toLowerCase().includes(searchTerm) ||
            (emp.role || '').toLowerCase().includes(searchTerm)
        );
    }, [assignableEmployees, assigneeSearchTerm]);

    const handleAssigneeChange = (event) => {
        const options = event.target.options;
        const selectedUids = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                const isValidEmployee = assignableEmployees.some(emp => emp.id === options[i].value);
                if (isValidEmployee) {
                    selectedUids.push(options[i].value);
                }
            }
        }
        setAssignedTo(selectedUids);
    };

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const initializeForm = () => {
            const now = new Date();
            if (eventData) {
                setTitle(eventData.title || '');
                setServiceType(eventData.serviceType || '');
                setStart(safeDate(eventData.start, now));
                setEnd(safeDate(eventData.end, new Date(now.getTime() + 3600000)));
                setAllDay(eventData.allDay ?? true);
                setNotes(eventData.notes || '');
                setAssignedTo(Array.isArray(eventData.assignedTo) ? eventData.assignedTo : []);
            } else {
                const defaultStart = slotInfo?.start ? safeDate(slotInfo.start) : now;
                const defaultEnd = slotInfo?.end ? safeDate(slotInfo.end, new Date(defaultStart.getTime() + 3600000)) : new Date(defaultStart.getTime() + 3600000);
                setTitle('');
                setServiceType(serviceTypes[0]?.key || '');
                setStart(defaultStart);
                setEnd(defaultEnd);
                setAllDay(isFullDaySlot(defaultStart, defaultEnd));
                setNotes('');
                setAssignedTo([]);
            }
            setError('');
            setAssigneeSearchTerm('');
            setIsProcessing(false); // Reset processing state when modal opens/re-initializes
        };
        initializeForm();
    }, [isOpen, eventData, slotInfo]);

    const isFullDaySlot = (startDate, endDate) => {
      try {
          const start = safeDate(startDate);
          const end = safeDate(endDate);
          if (start.getHours() === 0 && start.getMinutes() === 0 && start.getSeconds() === 0 &&
              end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0) {
              // Check if the difference is exactly 24 hours or if start and end are the same day at midnight
              const diff = end.getTime() - start.getTime();
              if (diff === 0) return true; // Single day clicked in month view often gives same start/end at midnight
              if (diff === 24 * 60 * 60 * 1000) return true;
          }
          return false;
      } catch (e) {
          console.error('Full day detection error:', e);
          return false;
      }
  };
    
    const handleDateChange = (value, type) => {
        const newDate = new Date(value);
        if (!isValidDate(newDate)) return;
        if (type === 'start') {
            setStart(newDate);
            if (end < newDate && !allDay) { // Only auto-adjust end if not all-day and start is after end
                setEnd(new Date(newDate.getTime() + 3600000)); // Default to 1 hour after start
            } else if (allDay && new Date(formatDateForInput(end)) < new Date(formatDateForInput(newDate))) {
                setEnd(new Date(newDate)); // For all-day, if start moves past end, make end same as start
            }
        } else {
            setEnd(newDate);
        }
    };
    
    const formatDateTimeForInput = (date, isAllDay) => {
        const d = safeDate(date);
        if (!d) return '';
        const pad = (n) => n.toString().padStart(2, '0');
        if (isAllDay) {
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        }
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    // Simplified formatDateForInput for 'date' type input, ensuring local date
     const formatDateForInput = (date) => {
        if (!(date instanceof Date) || isNaN(date)) return '';
        try {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (e) {
            console.error("Error formatting date for input:", e);
            return '';
        }
    };


    const validateForm = () => {
        const errors = [];
        if (!title.trim()) errors.push('Title is required');
        if (!serviceType) errors.push('Service type is required');
        
        const startDate = safeDate(start);
        let endDateVal = safeDate(end);

        if (allDay) {
            const tempEnd = new Date(endDateVal);
            tempEnd.setHours(23,59,59,999); // Consider full day for comparison
            if (tempEnd < startDate) errors.push('End date must be on or after start date for all-day events');
        } else {
            if (endDateVal <= startDate) errors.push('End time must be after start time');
        }
        
        if (errors.length) {
            setError(errors.join('. '));
            return true; // Indicates validation error
        }
        setError('');
        return false; // No error
    };

    const createEventDataForSave = () => {
        const s = safeDate(start);
        let e = safeDate(end);

        if (allDay) {
            s.setHours(0, 0, 0, 0);
            // For FullCalendar, an all-day event's end is exclusive.
            // If user selected same day for start/end, make end start of next day.
            // If user selected different days, end should be start of day AFTER last selected day.
            e = new Date(s); // Start with the start date
            if (formatDateForInput(start) === formatDateForInput(end)) { // Single all-day event
                e.setDate(s.getDate() + 1);
            } else { // Multi-day all-day event, end date is already correct from picker
                e = new Date(end); // Use the chosen end date
                e.setDate(e.getDate() + 1); // Make it exclusive for FullCalendar
            }
            e.setHours(0,0,0,0);

        }

        return {
            title: title.trim(),
            serviceType,
            start: s, // Pass JS Date
            end: e,   // Pass JS Date
            allDay,
            notes: notes.trim(),
            assignedTo,
        };
    };
    
    const handleSave = async (e) => {
        e.preventDefault();
        if (!isMounted.current) return;
        if (validateForm()) return; // Stop if validation fails

        setIsProcessing(true);
        try {
            const eventPayload = createEventDataForSave();
            if (eventData?.id) {
                await onSave(eventData.id, eventPayload);
            } else {
                await onSave(eventPayload);
            }
            onClose(); // Close modal on successful save
        } catch (err) {
            if (isMounted.current) setError(err.message || 'An error occurred while saving');
        } finally {
            if (isMounted.current) setIsProcessing(false);
        }
    };

    const handleDeleteClick = async () => {
        if (!eventData || !eventData.id || isProcessing || !onDelete) return;
        if (!isMounted.current) return;
        
        setIsProcessing(true);
        setError('');
        try {
            await onDelete(eventData.id);
            onClose(); // Close modal on successful delete
        } catch (err) {
            if(isMounted.current) setError(err.message || "Failed to delete event.");
        } finally {
            if(isMounted.current) setIsProcessing(false);
        }
    };


    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <h2 className={styles.modalTitle}>{eventData ? 'Edit Event' : 'New Event'}</h2>
                
                {error && <div className={styles.errorMessage}>{error}</div>}

                <form onSubmit={handleSave} className={styles.form}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="event-title">Title *</label>
                        <input
                            id="event-title"
                            type="text"
                            className={styles.formInput} // Apply common style
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            disabled={isProcessing}
                            placeholder="e.g., Client Meeting"
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="event-serviceType">Service Type *</label>
                        <select
                            id="event-serviceType"
                            className={styles.formInput} // Apply common style
                            value={serviceType}
                            onChange={(e) => setServiceType(e.target.value)}
                            required
                            disabled={isProcessing}
                        >
                            <option value="">Select a service type</option>
                            {serviceTypes.map(({ key, label }) => (
                                <option key={key} value={key}>{label}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="modal-assignTo">Assign To</label>
                        <input
                            type="text"
                            placeholder="Search team members..."
                            value={assigneeSearchTerm}
                            onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                            disabled={isProcessing || assignableEmployees.length === 0}
                            className={`${styles.formInput} ${styles.searchInput}`} // Combine styles
                        />
                        <select
                            id="modal-assignTo"
                            multiple
                            value={assignedTo}
                            onChange={handleAssigneeChange}
                            size="3"
                            disabled={isProcessing || assignableEmployees.length === 0}
                            className={styles.multiSelectInput} 
                        >
                            {assignableEmployees.length === 0 && (
                                <option disabled>
                                    {employees.length === 0 
                                        ? "No team members found" 
                                        : "No assignable staff (trainees excluded)"}
                                </option>
                            )}
                            {filteredEmployees.length === 0 && assignableEmployees.length > 0 && (
                                <option disabled>No matches for "{assigneeSearchTerm}"</option>
                            )}
                            {filteredEmployees.map(emp => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.name} 
                                </option>
                            ))}
                        </select>
                        <small className={styles.selectHelpText}>
                            {assignableEmployees.length > 0 
                                ? "Ctrl/Cmd-click to select multiple"
                                : ""}
                        </small>
                    </div>

                    <div className={styles.dateGrid}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="event-start">Start *</label>
                            <input
                                id="event-start"
                                type={allDay ? 'date' : 'datetime-local'}
                                className={styles.formInput} // Apply common style
                                value={formatDateTimeForInput(start, allDay)}
                                onChange={(e) => handleDateChange(e.target.value, 'start')}
                                required
                                disabled={isProcessing}
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="event-end">End *</label>
                            <input
                                id="event-end"
                                type={allDay ? 'date' : 'datetime-local'}
                                className={styles.formInput} // Apply common style
                                value={formatDateTimeForInput(end, allDay)}
                                onChange={(e) => handleDateChange(e.target.value, 'end')}
                                required
                                disabled={isProcessing}
                                min={!allDay ? formatDateTimeForInput(start, false) : formatDateForInput(start)}
                            />
                        </div>

                        <div className={`${styles.inputGroup} ${styles.allDayCheck}`}>
                            <label htmlFor="event-allDay">
                                <input
                                    id="event-allDay"
                                    type="checkbox"
                                    checked={allDay}
                                    onChange={(e) => setAllDay(e.target.checked)}
                                    disabled={isProcessing}
                                />
                                All Day Event
                            </label>
                        </div>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="event-notes">Notes</label>
                        <textarea
                            id="event-notes"
                            className={styles.formInput} // Apply common style
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows="3"
                            disabled={isProcessing}
                            placeholder="Add any relevant details..."
                        />
                    </div>

                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            className={`${styles.formButton} ${styles.cancelButton}`}
                            onClick={onClose}
                            disabled={isProcessing}
                        >
                            Cancel
                        </button>
                        
                        {eventData && onDelete && (
                            <button
                                type="button"
                                onClick={handleDeleteClick}
                                disabled={isProcessing}
                                className={`${styles.formButton} ${styles.deleteButton}`}
                            >
                                {isProcessing ? 'Deleting...' : 'Delete'}
                            </button>
                        )}

                        <button
                            type="submit"
                            className={`${styles.formButton} ${styles.submitButton}`}
                            disabled={isProcessing}
                        >
                            {isProcessing ? <span className={styles.loadingSpinner}></span> : (eventData ? 'Update Event' : 'Save Event')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

EventModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    eventData: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        serviceType: PropTypes.string,
        start: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        end: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        allDay: PropTypes.bool,
        notes: PropTypes.string,
        assignedTo: PropTypes.arrayOf(PropTypes.string)
    }),
    slotInfo: PropTypes.shape({
        start: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        end: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
    }),
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    employees: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        role: PropTypes.string // Role is important for filtering
    }))
};

export default EventModal;